import React, { useRef } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { AviveLoadingIcon } from '@rsos/assets/icons';
import Box from '@rsos/components/capstone/base/Box';
import Button from '@rsos/components/capstone/base/Button/Button';
import AddressLocator from './AddressLocator';
import {
  StyledAviveSearchForm,
  StyledHeader,
  StyledForm,
  StyledFormGroup,
  ButtonBox,
  Label,
  Text,
} from './AviveSearchForm.styles';
import { Spin } from './AviveStandaloneStatus';
import PhoneField from './PhoneField';
import TextArea from './TextArea';

const validationSchema = Yup.object().shape({
  latitude: Yup.number().required(),
  longitude: Yup.number().required(),

  phone: Yup.string()
    .min(10, 'Phone number must be 10 digits or more')
    .required('Please enter a valid phone number.'),

  notes: Yup.string().max(500, 'Notes cannot exceed 500 characters'),
});

const AviveSearchForm = ({
  onClose = () => {},
  onSubmit = () => {},

  initialPhone = '',
  initialAddress = '',
  initialCoordinate = [],
}) => {
  const handleClose = () => onClose();

  const isMounted = useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <StyledAviveSearchForm>
      <Formik
        initialValues={{
          phone: '',
          latitude: null,
          longitude: null,
          notes: '',
        }}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          // Normalize the phone number to have a leading 1 if missing
          // to keep things nice and consistent higher up the stack.
          const phone = values.phone;
          const normalizedPhone = phone.startsWith('1') ? phone : `1${phone}`;
          const formattedValues = { ...values, phone: normalizedPhone };
          onSubmit(formattedValues, () => {
            try {
              if (isMounted.current) {
                actions.setSubmitting(false);
              }
            } catch {
              // supress: only way this erorrs if we're unmounted.
            }
          });
        }}
        validationSchema={validationSchema}
        validateOnMount
        validateOnChange
      >
        {({ errors, touched, isValid, isSubmitting }) => {
          const disabled = !isValid;
          return (
            <>
              <StyledHeader horizontal align="center" justify="space-between">
                <Text fontWeight="bold" size="normal" color="primaryText">
                  Dispatch Avive AED
                </Text>
              </StyledHeader>
              <StyledForm id="avive-form" data-testid="avive-search-form">
                <StyledFormGroup>
                  <Box mb={2}>
                    <Label>
                      <Text size="small">Add Phone Number</Text>
                      <PhoneField
                        placeholder="Phone Number"
                        name="phone"
                        errors={errors}
                        touched={touched}
                        initialValue={initialPhone}
                        disabled={initialPhone !== ''}
                      />
                    </Label>
                  </Box>
                  <Box>
                    <Label>
                      <Text size="small">Add Destination Address</Text>
                      <AddressLocator
                        initialValue={initialAddress}
                        initialCoordinate={initialCoordinate}
                        disabled={initialPhone !== ''}
                      />
                    </Label>
                  </Box>
                  <Box mt={1}>
                    <Label>
                      <Text size="small">Add Notes to Responder</Text>
                      <TextArea
                        data-testid="notes-field"
                        name="notes"
                        placeholder="(Optional)"
                        errors={errors}
                        touched={touched}
                      />
                    </Label>
                  </Box>
                </StyledFormGroup>
              </StyledForm>
              <ButtonBox horizontal align="center" justify="center">
                <Button
                  id="avive-cancel-button"
                  data-testid="cancel-button"
                  type="button"
                  btnType="secondary"
                  onClick={handleClose}
                  disabled={disabled || isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={disabled || isSubmitting}
                  data-testid="submit-button"
                  form="avive-form"
                >
                  {isSubmitting ? (
                    <>
                      Sending
                      <Box ml="5px">
                        <Spin>
                          <AviveLoadingIcon height="12" />
                        </Spin>
                      </Box>
                    </>
                  ) : (
                    <>Make AED Request</>
                  )}
                </Button>
              </ButtonBox>
            </>
          );
        }}
      </Formik>
    </StyledAviveSearchForm>
  );
};

export default AviveSearchForm;
